import { useEffect, useRef, useState, useCallback } from 'react';
import api from '@/lib/api';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'agent';
  content: string;
  created_at: string;
  metadata?: { task_id?: string } | null;
}

export interface ChatConversation {
  id: string;
  title: string;
  last_message_preview: string | null;
  last_message_at: string | null;
  created_at: string;
}

interface UseAgentChatOptions {
  conversationId: string | null;
  pollIntervalMs?: number;
}

export function useAgentChat({ conversationId, pollIntervalMs = 3000 }: UseAgentChatOptions) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [thinking, setThinking] = useState(false);

  const lastMessageIdRef = useRef<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load all messages for a conversation
  useEffect(() => {
    if (!conversationId) {
      setMessages([]);
      lastMessageIdRef.current = null;
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get<{ messages: ChatMessage[] }>(
          `/admin/agent/conversations/${conversationId}/messages`
        );
        if (cancelled) return;
        setMessages(res.messages);
        if (res.messages.length > 0) {
          lastMessageIdRef.current = res.messages[res.messages.length - 1].id;
        }
      } catch {
        // silently ignore
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [conversationId]);

  // Poll for new messages
  const pollMessages = useCallback(async () => {
    if (!conversationId) return;
    try {
      const after = lastMessageIdRef.current ?? '';
      const res = await api.get<{ messages: ChatMessage[] }>(
        `/admin/agent/conversations/${conversationId}/messages${after ? `?after=${after}` : ''}`
      );
      if (res.messages.length > 0) {
        setMessages((prev) => [...prev, ...res.messages]);
        lastMessageIdRef.current = res.messages[res.messages.length - 1].id;
        setThinking(false);
      }
    } catch {
      // silently ignore poll errors
    }
  }, [conversationId]);

  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (!conversationId) return;
    pollRef.current = setInterval(pollMessages, pollIntervalMs);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [conversationId, pollMessages, pollIntervalMs]);

  // Send a message
  const sendMessage = async (content: string): Promise<ChatMessage | null> => {
    if (!content.trim() || !conversationId || sending) return null;

    const trimmed = content.trim();
    setSending(true);
    setThinking(true);

    const optimistic: ChatMessage = {
      id: `optimistic-${Date.now()}`,
      role: 'user',
      content: trimmed,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);

    try {
      const res = await api.post<{ message: ChatMessage }>(
        `/admin/agent/conversations/${conversationId}/messages`,
        { content: trimmed }
      );
      setMessages((prev) =>
        prev.map((m) => (m.id === optimistic.id ? res.message : m))
      );
      lastMessageIdRef.current = res.message.id;
      return res.message;
    } catch {
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setThinking(false);
      return null;
    } finally {
      setSending(false);
    }
  };

  return {
    messages,
    loading,
    sending,
    thinking,
    sendMessage,
  };
}
